'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Field,
  Input,
  SettingsEditDialog,
  SettingsRow,
} from '@terra/ui-design-system';
import { Edit03Icon } from '@terra/ui-icons';
import { ProxyPasswordInput } from './ProxyPasswordInput';
import {
  parseProxyIntegerDraft,
  toProxySettingDisplayValue,
  type ProxyIntegerBounds,
} from './proxy-setting-edit.model';

type ProxySettingEditRowBaseProps = {
  label: string;
  description?: string;
  tooltip?: string;
  placeholder?: string;
};

type ProxySettingEditRowProps = ProxySettingEditRowBaseProps &
  (
    | {
        kind: 'text' | 'password';
        value: string;
        onSave: (value: string) => void;
      }
    | ({
        kind: 'integer';
        value: number;
        onSave: (value: number) => void;
      } & ProxyIntegerBounds)
  );

export function ProxySettingEditRow(props: ProxySettingEditRowProps) {
  const { label, description, tooltip, placeholder } = props;
  const t = useTranslations('settings.proxyForm.edit');
  const tCommon = useTranslations('common');
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const display = toProxySettingDisplayValue({
    kind: props.kind,
    value: props.value,
  });
  const integerDraft =
    props.kind === 'integer'
      ? parseProxyIntegerDraft({ draft, min: props.min, max: props.max })
      : null;
  const isDraftInvalid = integerDraft !== null && !integerDraft.valid;
  const integerError =
    props.kind === 'integer'
      ? props.min !== undefined && props.max !== undefined
        ? t('integerRange', { min: props.min, max: props.max })
        : t('integerMin', { min: props.min ?? 0 })
      : undefined;

  const openDialog = () => {
    setDraft(String(props.value));
    setIsOpen(true);
  };

  const handleSubmit = () => {
    if (props.kind === 'integer') {
      if (integerDraft?.valid) {
        props.onSave(integerDraft.value);
        setIsOpen(false);
      }

      return;
    }

    props.onSave(draft.trim());
    setIsOpen(false);
  };

  return (
    <>
      <SettingsRow
        label={label}
        description={description}
        tooltip={tooltip}
        value={
          display.kind === 'value'
            ? display.text
            : display.kind === 'masked'
              ? t('passwordSet')
              : t('notSet')
        }
        action={{
          label: tCommon('edit'),
          icon: <Edit03Icon size={16} />,
          onClick: openDialog,
        }}
      />
      <SettingsEditDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        title={label}
        description={description}
        cancelText={tCommon('cancel')}
        submitText={tCommon('save')}
        isSubmitDisabled={isDraftInvalid}
        onSubmit={handleSubmit}
        contentMaxW="md"
      >
        <Field.Root invalid={isDraftInvalid && draft.trim().length > 0}>
          <Field.Label>{label}</Field.Label>
          {props.kind === 'password' ? (
            <ProxyPasswordInput
              value={draft}
              onChange={setDraft}
              placeholder={placeholder ?? ''}
            />
          ) : (
            <Input
              value={draft}
              type={props.kind === 'integer' ? 'number' : 'text'}
              inputMode={props.kind === 'integer' ? 'numeric' : undefined}
              min={props.kind === 'integer' ? props.min : undefined}
              max={props.kind === 'integer' ? props.max : undefined}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !isDraftInvalid) {
                  event.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder={placeholder}
            />
          )}
          {isDraftInvalid && draft.trim().length > 0 ? (
            <Field.ErrorText>{integerError}</Field.ErrorText>
          ) : null}
        </Field.Root>
      </SettingsEditDialog>
    </>
  );
}
